import { structure2mesh } from 'santa-mesh'
import {
	color,
	getDisplayedId,
	getFullId,
	getItemId,
	isDisplayedOnly,
	isRepeater,
	links,
	nameUUIDFromString,
	text,
	vectorImage,
} from '@wix/thunderbolt-commons'
import { BeckyFunctionLibrary, Experiments } from './types/becky'
import { getDockedStyles } from './dockedLayer'
import * as functionLibraryExtensions from './functionLibraryExtensions'
import { getFileExtension } from './siteAssetsModules/utils/reportingHelper'
import _ from 'lodash'
import { convertSeoModel } from '@wix/advanced-seo-utils/converters'
import { resolvePayload } from '@wix/advanced-seo-utils/renderer'
import { PageDefinition } from '@wix/thunderbolt-components'
import { BeckyFuncLibParams, FetchFn, MetricsReporter } from '@wix/thunderbolt-becky-types'
import { getMeshResult, getMeshResultForHoverBox, getMeshResultsForMasterPage } from './carmi/mesh/meshFunctionLibrary'

const globalCache = new WeakMap()
const fetchResultsCache = new WeakMap()

const stringifyAttrs = (attrs: Record<string, string>) => {
	return Object.keys(attrs)
		.map((k) => `${k}="${attrs[k]}"`)
		.join(' ')
}

const fetchUrlFactory = (
	fetch: FetchFn,
	runAsyncAndReport: MetricsReporter['runAsyncAndReport'],
	reportError: MetricsReporter['reportError']
) => {
	return function (
		this: BeckyFunctionLibrary,
		url: string,
		fallbackValue: string,
		updateStore: (value: string | null) => void
	): void {
		let pendingRequests = globalCache.get(this)
		let resultsCache = fetchResultsCache.get(this)
		if (!pendingRequests) {
			globalCache.set(this, {})
			pendingRequests = globalCache.get(this)
		}
		if (!resultsCache) {
			fetchResultsCache.set(this, {})
			resultsCache = fetchResultsCache.get(this)
		}
		if (resultsCache[url] || pendingRequests[url]) {
			return
		}
		pendingRequests[url] = true

		updateStore(null)
		runAsyncAndReport(
			() =>
				fetch(url)
					.then((res) => (res.ok ? res.text() : null))
					.catch((e) => {
						reportError(e)
						return null
					})
					.then((txt) => {
						resultsCache[url] = true
						updateStore(txt === null ? fallbackValue : txt)
					}),
			`fetchInBecky|${getFileExtension(url)}`
		)
	}
}

const buildSvgUrlFactory = () => {
	const addTrailingSlashIfMissing = (val: string) => (val.endsWith('/') ? val : `${val}/`)
	const getSvgBaseUrl = (mediaRootUrl: string) => `${addTrailingSlashIfMissing(mediaRootUrl)}shapes/`
	const svgIdToUri = (svgId: string) => {
		const partsArr = svgId.replace(/^.*\//, '').split('.')
		const version = partsArr[1] === 'v1' ? 1 : 2
		const svgHash = partsArr[2].replace(/svg_/i, '')
		const svgName = partsArr[3]
		return `${svgHash + (version === 1 ? `_svgshape.v1.${svgName}` : '')}.svg`
	}

	return {
		getSvgBaseUrl,
		buildSvgUrl: (mediaRootUrl: string, svgId: string) => {
			if (/^svgshape\.v[12]/.test(svgId)) {
				const svgUri = svgIdToUri(svgId)
				return `${getSvgBaseUrl(mediaRootUrl)}${svgUri}`
			}
			return `${getSvgBaseUrl(mediaRootUrl)}${svgId}`
		},
	}
}

const invoke = (cb: Function, ...args: Array<any>) => cb && cb(...args)

const invokeResolverFactory = (reportError: MetricsReporter['reportError']) => (
	cb: Function,
	extensionName: string,
	compId: string,
	compType: string,
	...resolverArgs: Array<any>
) => {
	try {
		return cb && cb(...resolverArgs)
	} catch (error) {
		const wrappedError = new Error(
			`Error resolving ${extensionName} for component ${compId} of type ${compType}: ${error?.message}`
		)

		console.error(wrappedError, error)
		reportError(wrappedError)

		return {}
	}
}

const castToNumber = (value: string) => Number(value)

const regExpMatch = (toFind: string, regExpStr: string) => toFind.match(new RegExp(regExpStr))

const rgbToHex = (rgbString: string) => {
	const componentToHex = (c: string) => {
		// @ts-ignore
		const hex = c.toString(16)
		return hex.length === 1 ? '0' + hex : hex
	}

	const [r, g, b] = rgbString.split(',')
	return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

const getUndefined = () => void 0

const seoMergeBlob = (
	originalPageData: PageDefinition['unresolvedData'],
	translatedPageData: PageDefinition['unresolvedData'],
	experiments: Experiments
) => {
	const original = convertSeoModel(originalPageData?.advancedSeoData || '')
	const translated = convertSeoModel(translatedPageData?.advancedSeoData || '')
	const shouldMergeAdvancedSeoData = !experiments['specs.thunderbolt.dontMergeAdvancedSeoDataForML']
	const nonMergedAdvancedSeoData = translatedPageData ? translated : original
	const advancedSeoData = shouldMergeAdvancedSeoData
		? resolvePayload([original, translated])
		: nonMergedAdvancedSeoData
	return { ...originalPageData, ...translatedPageData, advancedSeoData: JSON.stringify(advancedSeoData) }
}

const getColorIndex = (colorName: string) => Number(colorName.split('_').pop())

const createFunctionLibrary = ({ metricsReporter, fetch }: BeckyFuncLibParams): BeckyFunctionLibrary => {
	const { runAndReport, runAsyncAndReport, reportError } = metricsReporter

	const { buildSvgUrl, getSvgBaseUrl } = buildSvgUrlFactory()

	return {
		isArray: _.isArray,
		sortBy: _.sortBy,
		trim: (str: string) => str.trim(),
		removeHash: (value?: string) => value && value.replace('#', ''),
		replace: (value: string, searchValue: string | RegExp, replaceValue: string) =>
			value.replace(searchValue, replaceValue),
		split: (str: string, delimiter: string) => str.split(delimiter),
		structure2mesh,
		getWixcodeConnection: (role: string, compId: string, controllerId: string) => ({
			controllerId,
			[role]: compId,
		}),
		kebabCase: _.kebabCase,
		includes: (str: string, searchString: string) => str.includes(searchString),
		merge: (objects: Array<any>) => _.merge({}, ...objects),
		parseInt: (val: string) => parseInt(val, 10),
		parseJSON: (val: string) => {
			if (!val) {
				return val
			}

			try {
				return JSON.parse(val)
			} catch (e) {
				if (process.env.NODE_ENV === 'development') {
					console.warn('parseJSON failed parsing value:', val)
				}
				return null
			}
		},
		serializeJSON: (val: object) => JSON.stringify(val),
		doubleQuotesEscapeForIE: (str: string) => str.replace(/''/g, "'"),
		encodeURIComponent,
		mathFloor: Math.floor,
		parseFloat,
		getDockedStyles,
		fetchUrl: fetchUrlFactory(fetch, runAsyncAndReport, reportError),
		buildSvgUrl,
		getSvgBaseUrl,
		stringifyAttrs,
		invoke,
		invokeResolver: invokeResolverFactory(reportError),
		regExpMatch,
		castToNumber,
		getUndefined,
		rgbToHex,
		getColorIndex,
		...vectorImage,
		...color,
		...links,
		...text,
		getFullId,
		getItemId,
		getDisplayedId,
		isDisplayedOnly,
		isRepeater,
		runAsyncAndReport,
		runAndReport,
		...functionLibraryExtensions,
		seoMergeBlob,
		getMeshResult,
		getMeshResultsForMasterPage,
		getMeshResultForHoverBox,
		nameUUIDFromString,
	}
}

export { createFunctionLibrary }
